import {useContext, useEffect, useMemo, useState} from 'react';
import {DDEXPageContext} from './DDEXMainPage';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import ProDescriptions from "@ant-design/pro-descriptions";
import {Button, Card, Col, message, Row, Space, Spin, Tag, Tooltip, Typography} from "antd";
import {ReloadOutlined, WarningOutlined} from '@ant-design/icons';
import axiosApiInstance from '../../../api/axiosClient';
import {useLocation, useParams} from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DraggableArtistNameTag } from './DraggableArtistNameTag';
import { MenuOutlined } from '@ant-design/icons';

dayjs.extend(duration);

const formatDuration = (duration: number) => {
    // Extract whole minutes
    const minutes = Math.floor(duration / 60);

    // Extract remaining whole seconds
    const seconds = Math.floor(duration % 60);

    // Extract milliseconds
    const milliseconds = Math.round((duration % 1) * 1000);

    // Format the result as mm:ss:SSS
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    const formattedMilliseconds = String(milliseconds).padStart(3, '0');

    return `${formattedMinutes}:${formattedSeconds}.${formattedMilliseconds}`;
};


const parseDuration = (durationString: string) => {
    if (!isNaN(+durationString)) {
        return dayjs.duration(+durationString);
    }
    if (!durationString) {
        return dayjs.duration(0);
    }
    // Split the duration string into minutes, seconds, and milliseconds
    const [minutes, secondsWithMillis] = durationString.split(':');
    const [seconds, milliseconds] = secondsWithMillis.split('.');

    // Create a Day.js duration object
    const dur = dayjs.duration({
        minutes: parseInt(minutes, 10),
        seconds: parseInt(seconds, 10),
        milliseconds: parseInt(milliseconds, 10)
    });

    return dur;
};

const StepsFormThirdPage = () => {
    const [releaseInfoData, setReleaseInfoData] = useState<API.MusicDistribution.MusicRelease>();
    const [artTracksData, setArtTracksData] = useState<API.MusicDistribution.ArtTrack[]>();

    const [isOrderChanged, setIsOrderChanged] = useState(false);
    const [isSavingOrder, setIsSavingOrder] = useState(false);
    // State for preview UPC and ISRCs
    const [previewUPC, setPreviewUPC] = useState<string>('');
    const [previewISRCs, setPreviewISRCs] = useState<Record<string, string>>({});
    const [validationWarnings, setValidationWarnings] = useState<API.MusicDistribution.ValidationError[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const {id} = useParams()

    const originalArtistOrder = useMemo(
        () => releaseInfoData?.artists?.map(artist => artist.key_name) || [],
        [releaseInfoData]
    );

    // Function to fetch release data including preview UPC and ISRCs
    const fetchReleaseData = async () => {
        const releaseId = id;
        if (!releaseId) return;

        setLoading(true);
        try {
            // Call the previews endpoint to get preview data
            const response = await axiosApiInstance.get(`/api/products/releases/${releaseId}/previews`);
            if (response.data) {
                setReleaseInfoData(response.data.release);
                setArtTracksData(response.data.release.art_tracks || []);
                setPreviewUPC(response.data.preview_upc || '');
                setPreviewISRCs(response.data.preview_isrcs || {});
                setValidationWarnings(response.data.validation_warnings || []);
            }
        } catch (error) {
            console.error('Error fetching release data:', error);
        } finally {
            setLoading(false);
        }
    };

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 5,
        },
      })
    );

    const createArtistDisplayString = (main_artists: string[]): string => {
        const mainStr = main_artists.join(', ');
        return `${mainStr}`;
    };

    const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

        if (over && active.id !== over.id && releaseInfoData?.artists) {
            const oldIndex = releaseInfoData.artists.findIndex((artist) => artist.key_name === active.id);
            const newIndex = releaseInfoData.artists.findIndex((artist) => artist.key_name === over.id);

            const reorderedArtists = arrayMove(releaseInfoData.artists, oldIndex, newIndex);

            // Update artist_display based on the new order
            const reorderedMainArtistNames = reorderedArtists.map(a => a.key_name);
            const newArtistDisplay = createArtistDisplayString(
                reorderedMainArtistNames
            );

            // Create a new updated release info object to avoid direct mutation
            const updatedReleaseInfo = {
                ...releaseInfoData,
                artists: reorderedArtists,
                artist_display: newArtistDisplay,
            };

            setReleaseInfoData(updatedReleaseInfo as API.MusicDistribution.MusicRelease);
            setIsOrderChanged(true); // Mark that the order has been changed
        }
    };

    // New function to save just the artist order
    const handleSaveOrder = async () => {
        if (!releaseInfoData?.id || !releaseInfoData.artists) {
            message.error("Release ID or artists are missing.");
            return;
        }
        setIsSavingOrder(true);
        try {
            // The payload only needs the ordered list of artist key_names
            const payload = {
            artists: releaseInfoData.artists.map(artist => ({ key_name: artist.key_name })),
            artist_display: releaseInfoData.artist_display,
            };
            console.log("Sending this payload to /update-artist-order:", JSON.stringify(payload));
            
            // It's good practice to have a dedicated endpoint for this small update
            await axiosApiInstance.post(
                `/api/products/releases/${releaseInfoData.id}/update-artist-order`, 
                payload
            );
            
            message.success("Artist order saved successfully!");
            await fetchReleaseData(); 
            setIsOrderChanged(false);
        } catch (error) {
            console.error("Failed to save artist order:", error);
            message.error("Failed to save artist order.");
        } finally {
            setIsSavingOrder(false);
        }
    };

    useEffect(() => {
        // Fetch release data when component mounts
        // Set loading to true before fetching data
        setLoading(true);
        fetchReleaseData();
    }, []);
    const {Title, Text} = Typography;

    // Helper function to render field value with proper formatting
    const renderFieldValue = (value, isPreview = false, previewMessage = "") => {
        if (value) {
            return value;
        } else if (isPreview && previewMessage) {
            return (
                <Tooltip title={previewMessage}>
                    <Tag color="warning" icon={<WarningOutlined/>}>
                        {previewMessage}
                    </Tag>
                </Tooltip>
            );
        } else {
            return <Text type="secondary">N/A</Text>;
        }
    };

    return (
        <Space direction="vertical" size="large" style={{width: '100%'}}>
            <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
                <Button
                    icon={<ReloadOutlined/>}
                    onClick={fetchReleaseData}
                    loading={loading}
                    type="primary"
                >
                    Reload
                </Button>
            </div>

            <Spin spinning={loading} tip="Loading data...">
                <Space direction="vertical" size="large" style={{width: '100%'}}>
                    {/* Validation Warnings */}
                    {validationWarnings.length > 0 && (
                        <Card
                            title={
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <Title level={4} style={{margin: 0, color: '#faad14'}}>Validation Warnings</Title>
                                </div>
                            }
                        >
                            <ul style={{paddingLeft: 20}}>
                                {validationWarnings.map((warning, index) => (
                                    <li key={index} style={{marginBottom: 8}}>
                                        <Text strong>{warning.group === 'track' ? 'Track' : 'Release'}:</Text> {warning.message} <Text type="secondary">({warning.field})</Text>
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    )}
                    {/* Release Details Card */}
                    <Card
                        title={
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Title level={4} style={{margin: 0}}>Release Details</Title>
                            </div>
                        }
                    >
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <ProDescriptions column={1} title="Basic Information" bordered>
                                    <ProDescriptions.Item label="Release Title">
                                        {renderFieldValue(releaseInfoData?.name)}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Version/Subtitle">
                                        {renderFieldValue(releaseInfoData?.subtitle)}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="UPC/EAN">
                                        {releaseInfoData?.upc_ean ? (
                                            <Tag color="success">{releaseInfoData.upc_ean}</Tag>
                                        ) : previewUPC ? (
                                            <Tooltip
                                                title="This is a preview UPC. It will be assigned when the release is verified.">
                                                <Tag color="warning" icon={<WarningOutlined/>}>
                                                    {previewUPC}
                                                </Tag>
                                            </Tooltip>
                                        ) : (
                                            <Text type="secondary">N/A</Text>
                                        )}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Format">
                                        {renderFieldValue(releaseInfoData?.type)}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="No. of Tracks">
                                        {renderFieldValue(artTracksData?.length)}
                                    </ProDescriptions.Item>
                                </ProDescriptions>
                            </Col>
                            <Col span={12}>
                                <ProDescriptions column={1} title="Artists Information" bordered>
                                    <ProDescriptions.Item label="Main Artists">
                                        <div style={{ marginBottom: '8px' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                <MenuOutlined style={{ marginRight: '4px' }} />
                                                Drag artists to reorder them
                                            </Text>
                                        </div>
                                        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                                            <SortableContext
                                                items={releaseInfoData?.artists?.map(a => a.key_name) || []}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                  {(releaseInfoData?.artists || []).map(artist => (
                                                      <DraggableArtistNameTag
                                                        key={artist.key_name}
                                                        id={artist.key_name}
                                                        name={artist.key_name}
                                                      />
                                                  ))}
                                                </div>
                                            </SortableContext>
                                        </DndContext>
                                        {isOrderChanged && (
                                            <Button
                                                type="primary"
                                                onClick={handleSaveOrder}
                                                loading={isSavingOrder}
                                                style={{marginTop: 8}}
                                            >
                                                Save Artist Order
                                            </Button>
                                        )}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Featuring Artists">
                                        {renderFieldValue(releaseInfoData?.featuring_artists?.map(artist => artist.key_name).join(', '))}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Artist Display Name">
                                        {renderFieldValue(releaseInfoData?.artist_display)}
                                    </ProDescriptions.Item>
                                    {/*<ProDescriptions.Item label="Various Artists">*/}
                                    {/*    {renderFieldValue(releaseInfoData?.is_compilation)}*/}
                                    {/*</ProDescriptions.Item>*/}
                                </ProDescriptions>
                            </Col>
                            <Col span={12}>
                                <ProDescriptions column={1} title="Label Information" bordered>
                                    <ProDescriptions.Item label="Label">
                                        {renderFieldValue(releaseInfoData?.label?.name)}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Asset Label">
                                        {renderFieldValue(releaseInfoData?.asset_label?.name)}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Genre">
                                        {renderFieldValue(releaseInfoData?.genre)}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Subgenre">
                                        {renderFieldValue(releaseInfoData?.sub_genre)}
                                    </ProDescriptions.Item>
                                </ProDescriptions>
                            </Col>
                            <Col span={12}>
                                <ProDescriptions column={1} title="Release Dates & Territories" bordered>
                                    <ProDescriptions.Item label="Digital Release Date">
                                        {releaseInfoData && typeof releaseInfoData.digital_release_date == 'string' ?
                                            dayjs(releaseInfoData.digital_release_date).format('YYYY-MM-DD') :
                                            // isDayjs(releaseInfoData?.digital_release_date) ?
                                            //     releaseInfoData.digital_release_date.format('YYYY-MM-DD') :
                                            <Text type="secondary">N/A</Text>}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Original Release Date">
                                        {releaseInfoData && typeof releaseInfoData.original_release_date == 'string' ?
                                            dayjs(releaseInfoData.original_release_date).format('YYYY-MM-DD') :
                                            // isDayjs(releaseInfoData?.original_release_date) ?
                                            //     releaseInfoData.original_release_date.format('YYYY-MM-DD') :
                                            <Text type="secondary">N/A</Text>}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Production Year">
                                        {releaseInfoData && typeof releaseInfoData.production_year == 'string' ?
                                            dayjs(releaseInfoData.production_year).format('YYYY') :
                                            // isDayjs(releaseInfoData?.production_year) ?
                                            //     releaseInfoData.production_year.format('YYYY') :
                                            <Text type="secondary">N/A</Text>}
                                    </ProDescriptions.Item>
                                    <ProDescriptions.Item label="Territories">
                                        {renderFieldValue(releaseInfoData?.territories)}
                                    </ProDescriptions.Item>
                                </ProDescriptions>
                            </Col>
                            <Col span={24}>
                                <Row gutter={[16, 0]}>
                                    <Col span={8}>
                                        <ProDescriptions column={1} title="Additional Information" bordered>
                                            <ProDescriptions.Item label="Price Tier">
                                                {renderFieldValue(releaseInfoData?.price_tier)}
                                            </ProDescriptions.Item>
                                        </ProDescriptions>
                                    </Col>
                                    <Col span={16}>
                                        <ProDescriptions column={1} bordered>
                                            <ProDescriptions.Item label="Cover Art">
                                                {renderFieldValue(releaseInfoData?.album_cover_filename)}
                                            </ProDescriptions.Item>
                                        </ProDescriptions>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Card>

                    {/* Art Tracks Card */}
                    <Card
                        title={
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <Title level={4} style={{margin: 0}}>Art Track Details</Title>
                            </div>
                        }
                    >
                        {artTracksData && artTracksData.length > 0 ? artTracksData.map((track, index) => (
                            <div key={track.id || index} style={{
                                marginBottom: 8,
                                paddingBottom: 8,
                                borderBottom: index < (artTracksData.length - 1) ? '1px solid #f0f0f0' : 'none'
                            }}>
                                <Row align="middle" gutter={[8, 0]}>
                                    <Col span={14}>
                                        <Text strong>{track.title || 'Untitled Track'}</Text>
                                    </Col>
                                    <Col span={10}>
                                        <Space>
                                            <Tag>Vol: {renderFieldValue(track.volume_number) || '1'}</Tag>
                                            <Tag>Track: {track.track_number}</Tag>
                                            {track.isrc ? (
                                                <Tag color="success">{track.isrc}</Tag>
                                            ) : previewISRCs[track.id] ? (
                                                <Tooltip
                                                    title="This is a preview ISRC. It will be assigned when the release is verified.">
                                                    <Tag color="warning" icon={<WarningOutlined/>}>
                                                        {previewISRCs[track.id]}
                                                    </Tag>
                                                </Tooltip>
                                            ) : null}
                                        </Space>
                                    </Col>
                                </Row>
                                <Row gutter={[8, 0]} style={{marginTop: 4}}>
                                    <Col span={14}>
                                        <Text type="secondary">Artist: </Text>
                                        <Text>{renderFieldValue(track.main_artists?.map(artist => artist.key_name).join(', '))}</Text>
                                    </Col>
                                    <Col span={10}>
                                        <Text type="secondary">Filename: </Text>
                                        {track.filename ? (
                                            <Tag color="processing" style={{
                                                maxWidth: '100%',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {track.filename}
                                            </Tag>
                                        ) : (
                                            <Text type="secondary">N/A</Text>
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        )) : null}
                        {(!artTracksData || artTracksData.length === 0) && (
                            <div style={{textAlign: 'center', padding: '20px'}}>
                                <Text type="secondary">No tracks available</Text>
                            </div>
                        )}
                    </Card>
                </Space>
            </Spin>
        </Space>
    );
};

export default StepsFormThirdPage;
